// Hook React para status de rede.
// @capacitor/network primeiro (com tipo de conexão), fallback navigator.onLine.

import { useEffect, useState } from 'react';
import { Network, type ConnectionStatus } from '@capacitor/network';

export interface NetworkInfo {
  online: boolean;
  type: 'wifi' | 'cellular' | 'none' | 'unknown';
}

function normalize(s: ConnectionStatus | undefined): NetworkInfo {
  if (!s) {
    const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
    return { online, type: online ? 'unknown' : 'none' };
  }
  let type: NetworkInfo['type'] = 'unknown';
  const ct = String(s.connectionType);
  if (ct === 'wifi') type = 'wifi';
  else if (ct === 'cellular' || ct === '2g' || ct === '3g' || ct === '4g' || ct === '5g') type = 'cellular';
  else if (ct === 'none') type = 'none';
  return { online: !!s.connected, type };
}

export function useNetwork(): NetworkInfo {
  const [info, setInfo] = useState<NetworkInfo>(() => ({
    online: typeof navigator !== 'undefined' ? navigator.onLine : true,
    type: 'unknown',
  }));

  useEffect(() => {
    let cancelled = false;
    let removeListener: (() => Promise<void>) | (() => void) | null = null;

    const init = async () => {
      try {
        const cur = await Network.getStatus();
        if (!cancelled) setInfo(normalize(cur));
        const handle = await Network.addListener('networkStatusChange', (s) => {
          if (!cancelled) setInfo(normalize(s));
        });
        removeListener = handle.remove;
      } catch {
        // fallback browser
        const onOnline = () =>
          !cancelled && setInfo({ online: true, type: 'unknown' });
        const onOffline = () =>
          !cancelled && setInfo({ online: false, type: 'none' });
        if (typeof window !== 'undefined') {
          window.addEventListener('online', onOnline);
          window.addEventListener('offline', onOffline);
          removeListener = () => {
            window.removeEventListener('online', onOnline);
            window.removeEventListener('offline', onOffline);
          };
        }
      }
    };
    void init();

    return () => {
      cancelled = true;
      if (removeListener) {
        try {
          const r = removeListener();
          if (r && typeof (r as Promise<void>).then === 'function') {
            void (r as Promise<void>).catch(() => undefined);
          }
        } catch {
          // ignore
        }
      }
    };
  }, []);

  return info;
}

export default useNetwork;
